<template>
  <div class="page-container">
    <div class="content-wrapper">
      <h1 class="title">{{ titleText }}</h1>
      <p class="subtitle">
        Drag & drop files or click to upload. On mobile, you can take a picture directly.
      </p>

      <!-- File Drop Zone -->
      <div class="drop-zone-wrapper">
        <label
            class="drop-zone"
            :class="{
            'is-dragging': isDragging,
            'is-disabled': isUploading || !!fatalError
          }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleFileDrop"
        >
          <div class="drop-zone-content">
            <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-4-4V6a4 4 0 014-4h10a4 4 0 014 4v6a4 4 0 01-4 4H7z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m3-3H7"></path></svg>
            <p v-if="isUploading" class="status-text">Uploading...</p>
            <p v-else class="main-text"><span class="font-semibold">Click to upload</span> or drag and drop</p>
            <p class="hint-text">Images will be resized to a max of 2048px</p>
          </div>
          <input
              ref="fileInput"
              type="file"
              class="hidden"
              @change="handleFileSelect"
              multiple
              accept="image/png, image/jpeg"
              :disabled="isUploading || !!fatalError"
          />
        </label>
      </div>

      <!-- Status and Error Messages -->
      <div v-if="statusMessage || fatalError" class="status-box-wrapper">
        <div
            class="status-box"
            :class="{
            'is-success': statusMessage && statusMessage.type === 'success',
            'is-error': statusMessage && statusMessage.type === 'error' || fatalError,
            'is-info': statusMessage && statusMessage.type === 'info'
          }"
        >
          <p class="font-semibold">{{ (statusMessage || fatalError)?.title }}</p>
          <p class="text-sm">{{ (statusMessage || fatalError)?.message }}</p>
        </div>
      </div>
    </div>

    <!-- Hidden canvas element used for resizing images -->
    <canvas ref="resizeCanvas" style="display: none;"></canvas>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from '../services/axiosInstance'

// --- STATE ---

// Query params from original component
const testId = ref(null)
const token = ref(null)
const uploadType = ref('questions') // 'questions' | 'rubric'

// UI and upload state from dropzone example
const fileInput = ref(null)
const isDragging = ref(false)
const isUploading = ref(false)
const statusMessage = ref(null) // For temporary messages
const fatalError = ref(null) // For permanent errors like missing testId
const resizeCanvas = ref(null)
// --- LIFECYCLE ---

onMounted(async () => {
  parseUrlParams()
  if (!testId.value) {
    fatalError.value = {
      title: 'Configuration Error',
      message: 'The Test ID is missing from the URL. This page cannot be used.'
    }
    return
  }
  await autoLogin()
})

// --- COMPUTED PROPERTIES ---

const titleText = computed(() => {
  switch (uploadType.value) {
    case 'questions': return 'Upload Question Pages'
    case 'rubric': return 'Upload Answer Pages'
    default: return 'Upload Pages'
  }
})

// --- METHODS from Original Component (Authentication & Endpoints) ---

function parseUrlParams () {
  const urlParams = new URLSearchParams(window.location.search)
  testId.value = urlParams.get('testId')
  token.value = urlParams.get('token')
  const typeParam = urlParams.get('type')
  if (typeParam === 'questions' || typeParam === 'rubric') {
    uploadType.value = typeParam
  }
}

async function autoLogin () {
  if (!token.value) return
  try {
    await axios.post('/auto-login', { token: token.value })
  } catch (e) {
    console.warn('Auto-login error:', e)
    // Non-fatal, user might have a valid session cookie
  }
}

function apiPath (path) {
  return path.startsWith('/') ? path : `/${path}`
}

function getUploadEndpoint () {
  if (!testId.value) throw new Error('Missing testId')
  switch (uploadType.value) {
    case 'questions': return apiPath(`tests/${testId.value}/questions/pages`)
    case 'rubric': return apiPath(`tests/${testId.value}/answers/pages`)
    default: throw new Error(`Invalid upload type: ${uploadType.value}`)
  }
}

// --- METHODS from Dropzone Example (File Handling & Resizing) ---

function handleFileSelect (event) {
  const files = event.target.files
  if (files && files.length > 0) {
    uploadFiles(files)
  }
}

function handleFileDrop (event) {
  isDragging.value = false
  const files = event.dataTransfer.files
  if (files && files.length > 0) {
    uploadFiles(files)
  }
}

const resizeImage = (file) => {
  return new Promise((resolve, reject) => {
    // Using 2048px as the max dimension from the second example
    const maxDimension = 2048
    const img = new Image()
    img.onload = () => {
      let { width, height } = img

      // If image is already small enough, no need to resize
      if (width <= maxDimension && height <= maxDimension) {
        resolve(file)
        return
      }

      if (width > height) {
        height = Math.round(height * (maxDimension / width))
        width = maxDimension
      } else {
        width = Math.round(width * (maxDimension / height))
        height = maxDimension
      }

      const canvas = resizeCanvas.value
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(img, 0, 0, width, height)

      ctx.canvas.toBlob((blob) => {
        if (blob) {
          resolve(new File([blob], file.name, { type: 'image/jpeg' }))
        } else {
          reject(new Error('Canvas to Blob conversion failed.'))
        }
      }, 'image/jpeg', 0.92) // Using quality from original component
    }
    img.onerror = (err) => reject(err)
    img.src = URL.createObjectURL(file)
  })
}

// --- UPLOAD LOGIC (Combined) ---

const uploadFiles = async (fileList) => {
  if (isUploading.value || fatalError.value) return

  isUploading.value = true
  statusMessage.value = null

  const filesToUpload = Array.from(fileList).filter(f => f.type.startsWith('image/'))
  const totalFiles = filesToUpload.length

  if (totalFiles === 0) {
    statusMessage.value = {
      type: 'error',
      title: 'No Images Selected',
      message: 'Please select one or more image files (JPEG, PNG).'
    }
    isUploading.value = false
    return
  }

  let successCount = 0
  statusMessage.value = { type: 'info', title: 'Processing...', message: `Preparing ${totalFiles} file(s) for upload...` }

  for (const file of filesToUpload) {
    try {
      statusMessage.value = { type: 'info', title: 'Resizing...', message: `Processing "${file.name}"...` }
      const resizedBlob = await resizeImage(file)

      statusMessage.value = { type: 'info', title: 'Uploading...', message: `Uploading "${file.name}"...` }
      const formData = new FormData()
      formData.append('file', resizedBlob, 'page.jpg')

      const endpoint = getUploadEndpoint()
      await axios.post(endpoint, formData)

      successCount++
    } catch (error) {
      console.error('Upload error for file:', file.name, error)
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error'
      statusMessage.value = {
        type: 'error',
        title: `Upload Failed for "${file.name}"`,
        message: `Error: ${errorMsg}. Please try again.`
      }
      isUploading.value = false // Stop on first error
      return
    }
  }

  statusMessage.value = {
    type: 'success',
    title: 'Upload Complete',
    message: `${successCount} of ${totalFiles} file(s) uploaded successfully.`
  }

  setTimeout(() => {
    statusMessage.value = null
  }, 5000)

  isUploading.value = false
}
</script>

<style scoped>
.page-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  background-color: #f7fafc;
  padding: 1rem;
}

.content-wrapper {
  width: 100%;
  max-width: 36rem; /* equivalent to max-w-xl */
  text-align: center;
}

.title {
  font-size: 2.25rem; /* 3xl */
  font-weight: 800; /* font-bold */
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #718096;
  margin-bottom: 1.5rem;
}

.drop-zone-wrapper {
  margin: 0 auto;
}

.drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 16rem; /* h-64 */
  border: 2px dashed #d2d6dc;
  border-radius: 0.5rem;
  cursor: pointer;
  background-color: #ffffff;
  transition: background-color 0.2s, border-color 0.2s;
}

.drop-zone:hover {
  background-color: #f7fafc;
}

.drop-zone.is-dragging {
  border-color: #3490dc;
  background-color: #ebf8ff;
}

.drop-zone.is-disabled {
  cursor: not-allowed;
  background-color: #edf2f7;
  opacity: 0.7;
}

.drop-zone-content {
  padding: 1.25rem 1.5rem;
}

.icon {
  width: 2.5rem;
  height: 2.5rem;
  margin-bottom: 0.75rem;
  color: #a0aec0;
}

.main-text {
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #718096;
}
.font-semibold { font-weight: 600; }

.status-text {
  font-size: 1rem;
  color: #4a5568;
  font-weight: 600;
}

.hint-text {
  font-size: 0.75rem;
  color: #a0aec0;
}

.hidden {
  display: none;
}

.status-box-wrapper {
  margin-top: 1rem;
}

.status-box {
  padding: 1rem;
  border-radius: 0.375rem;
}

.status-box.is-success {
  background-color: #c6f6d5;
  color: #2f855a;
}
.status-box.is-error {
  background-color: #fed7d7;
  color: #c53030;
}
.status-box.is-info {
  background-color: #e2e8f0;
  color: #4a5568;
}

.status-box .text-sm {
  font-size: 0.875rem;
}
</style>
